import { Component } from '@theme/component';
import { StandardEvents } from '@shopify/events';
import { DrawerOpenEvent } from '@theme/theme-drawer';
import { ThemeEvents } from '@theme/events';

/**
 * A custom element that manages cart drawer behavior within a `<theme-drawer>`.
 *
 * Dialog lifecycle (open/close, squeeze, history, animations) is owned by `<theme-drawer>`.
 * The `cart:view` event is auto-dispatched by `CartItemsComponent` via the
 * `view-event-trigger="dialog"` attribute (see `snippets/cart-items-component.liquid`).
 * Cart count announcements are owned by `<header-actions>`.
 * This component handles the remaining cart-specific concerns: auto-open on add-to-cart,
 * sticky summary layout, and the installments CTA close-on-click.
 *
 * @extends {Component}
 */
class CartDrawerComponent extends Component {
  /** @type {number} */
  #summaryThreshold = 0.5;
  #recommendationRequestId = 0;

  /** @type {import('@theme/theme-drawer').ThemeDrawer | null} */
  get #themeDrawer() {
    return /** @type {import('@theme/theme-drawer').ThemeDrawer | null} */ (this.closest('theme-drawer'));
  }

  /** @type {HTMLDialogElement | null} */
  get #dialog() {
    return this.closest('dialog');
  }

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#handleCartLinesUpdate);
    this.#themeDrawer?.addEventListener(DrawerOpenEvent.eventName, this.#handleDrawerOpen);
    this.addEventListener(ThemeEvents.cartSectionRestored, this.#handleCartSectionRestored);

    // The restore path sets [open] before this module loads, so the
    // theme-drawer:open event will have already fired. Use the attribute
    // check so this works even before <theme-drawer> upgrades.
    if (this.#themeDrawer?.hasAttribute('open')) {
      this.#handleDrawerOpen();
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(StandardEvents.cartLinesUpdate, this.#handleCartLinesUpdate);
    this.#themeDrawer?.removeEventListener(DrawerOpenEvent.eventName, this.#handleDrawerOpen);
    this.removeEventListener(ThemeEvents.cartSectionRestored, this.#handleCartSectionRestored);
  }

  /**
   * Recomputes the sticky summary after a back/forward cache restore re-renders the cart items.
   *
   * On viewports at or above 990px an open drawer survives a navigation and is reopened during
   * parsing (see the restore script in `snippets/theme-drawer.liquid`), so no open event fires
   * on the way back and the measurement taken before the shopper left is the one still applied.
   * An empty drawer measured as unpinned would keep the checkout CTA scrolling away once the
   * restore fills it with line items.
   */
  #handleCartSectionRestored = () => {
    requestAnimationFrame(() => this.#updateStickyState());
  };

  /**
   * Handles the theme-drawer opening — updates sticky state and wires up the installments CTA.
   */
  #handleDrawerOpen = () => {
    this.#updateStickyState();
    this.#loadRecommendations();

    // Close cart drawer when installments CTA is clicked to avoid overlapping dialogs.
    // Re-queried on every open so it survives cart content re-renders that
    // replace the shopify-payment-terms shadow root.
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const cta = this.querySelector('shopify-payment-terms')?.shadowRoot?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', () => this.#themeDrawer?.close(), { once: true });
    });
  };

  /**
   * @param {import('@shopify/events').CartLinesUpdateEvent} event
   */
  #loadRecommendations = async (attempt = 0) => {
    const requestId = ++this.#recommendationRequestId;
    const recommendationHost = this.#getRecommendationHost();

    if (!recommendationHost || this.#isCartEmpty()) {
      if (recommendationHost) {
        recommendationHost.replaceChildren();
      }

      if (attempt < 4 && !this.#isCartEmpty()) {
        setTimeout(
          () => this.#loadRecommendations(attempt + 1),
          120
        );
      }

      return;
    }

    try {
      const cartResponse = await fetch(
        `${window.Shopify.routes.root}cart.js`,
        {
          headers: {
            Accept: 'application/json',
          },
          credentials: 'same-origin',
          cache: 'no-store',
        }
      );

      if (!cartResponse.ok) {
        if (attempt < 4) {
          setTimeout(
            () => this.#loadRecommendations(attempt + 1),
            150
          );
        }

        return;
      }

      const cart = await cartResponse.json();

      if (requestId !== this.#recommendationRequestId) {
        return;
      }

      const firstItem = cart.items?.[0];

      if (!firstItem?.product_id) {
        recommendationHost.replaceChildren();
        return;
      }

      let sourceProductType = firstItem.product_type || '';
      let sourceTags = [];

      if (firstItem.handle) {
        try {
          const productResponse = await fetch(
            `${window.Shopify.routes.root}products/${firstItem.handle}.js`,
            {
              headers: {
                Accept: 'application/json',
              },
              credentials: 'same-origin',
              cache: 'no-store',
            }
          );

          if (productResponse.ok) {
            const sourceProduct = await productResponse.json();

            if (requestId !== this.#recommendationRequestId) {
              return;
            }

            sourceProductType =
              sourceProduct.product_type ||
              sourceProductType ||
              '';

            sourceTags = Array.isArray(sourceProduct.tags)
              ? sourceProduct.tags
              : [];
          }
        } catch (error) {
          if (error?.name !== 'AbortError') {
            console.warn(
              '[cart-drawer] Could not load source product data:',
              error
            );
          }
        }
      }

      const recommendationsUrl =
        `${window.Shopify.routes.root}recommendations/products` +
        `?product_id=${firstItem.product_id}` +
        `&limit=10` +
        `&section_id=cart-drawer-recommendations`;

      const recommendationsResponse = await fetch(
        recommendationsUrl,
        {
          headers: {
            Accept: 'text/html',
          },
          credentials: 'same-origin',
          cache: 'no-store',
        }
      );

      if (!recommendationsResponse.ok) {
        if (attempt < 4) {
          setTimeout(
            () => this.#loadRecommendations(attempt + 1),
            180
          );
        }

        return;
      }

      const html = await recommendationsResponse.text();

      if (requestId !== this.#recommendationRequestId) {
        return;
      }

      const parser = new DOMParser();

      const parsedDocument = parser.parseFromString(
        html,
        'text/html'
      );

      const recommendations =
        parsedDocument.querySelector(
          '.mydex-cart-recommendations'
        );

      if (!recommendations) {
        recommendationHost.replaceChildren();

        if (attempt < 4) {
          setTimeout(
            () => this.#loadRecommendations(attempt + 1),
            220
          );
        }

        return;
      }

      const candidates = [
        ...recommendations.querySelectorAll(
          '.mydex-cart-recommendation'
        ),
      ];

      const cartProductIds = new Set(
        (cart.items || [])
          .map((item) => String(item.product_id))
          .filter(Boolean)
      );

      const normalizedSourceType =
        String(sourceProductType)
          .trim()
          .toLowerCase();

      const normalizedSourceTags = new Set(
        sourceTags
          .map((tag) =>
            String(tag)
              .trim()
              .toLowerCase()
          )
          .filter(Boolean)
      );

      /*
       * Score recommendations.
       *
       * Same product type = 100
       * Shared tag = 20 each
       */
      const scoredCandidates = candidates
        .map((candidate, index) => {
          const productId =
            candidate.dataset.productId || '';

          const productType =
            String(
              candidate.dataset.productType || ''
            )
              .trim()
              .toLowerCase();

          const tags =
            String(
              candidate.dataset.productTags || ''
            )
              .split('|')
              .map((tag) =>
                tag
                  .trim()
                  .toLowerCase()
              )
              .filter(Boolean);

          if (
            !productId ||
            cartProductIds.has(String(productId))
          ) {
            return null;
          }

          let score = 0;

          if (
            normalizedSourceType &&
            productType &&
            productType === normalizedSourceType
          ) {
            score += 100;
          }

          for (const tag of tags) {
            if (normalizedSourceTags.has(tag)) {
              score += 20;
            }
          }

          return {
            element: candidate,
            score,
            index,
          };
        })
        .filter(Boolean);

      scoredCandidates.sort((a, b) => {
        if (b.score !== a.score) {
          return b.score - a.score;
        }

        return a.index - b.index;
      });

      /*
       * First use related products.
       * Then use unrelated Shopify recommendations
       * only when necessary to fill the two spaces.
       */
      const relevantCandidates =
        scoredCandidates.filter(
          (candidate) => candidate.score > 0
        );

      const fallbackCandidates =
        scoredCandidates.filter(
          (candidate) => candidate.score === 0
        );

      const selectedCandidates = [
        ...relevantCandidates,
        ...fallbackCandidates,
      ].slice(0, 2);

      const selectedElements =
        selectedCandidates.map(
          (candidate) => candidate.element
        );

      if (!selectedElements.length) {
        recommendationHost.replaceChildren();
        return;
      }

      /*
       * Clone the original Liquid container so all
       * variant data and product form markup remain intact.
       */
      const finalRecommendations =
        recommendations.cloneNode(true);

      const finalList =
        finalRecommendations.querySelector(
          '.mydex-cart-recommendations__list'
        );

      if (finalList) {
        finalList.replaceChildren(
          ...selectedElements.map(
            (element) => element.cloneNode(true)
          )
        );
      }

      const currentHost =
        this.#getRecommendationHost();

      if (currentHost) {
        currentHost.replaceChildren(
          finalRecommendations
        );

        /*
         * The Liquid section is inserted dynamically.
         * Scripts inside fetched HTML do not execute automatically,
         * so initialize variant selectors here.
         */
        this.#initializeRecommendationVariants(
          finalRecommendations
        );
      }
    } catch (error) {
      if (error?.name !== 'AbortError') {
        if (attempt < 4) {
          setTimeout(
            () => this.#loadRecommendations(attempt + 1),
            200
          );
        } else {
          console.warn(
            '[cart-drawer] Failed to load recommendations:',
            error
          );
        }
      }
    }
  };


  #initializeRecommendationVariants = (container) => {
    const cards =
      container?.querySelectorAll(
        '.mydex-cart-recommendation'
      );

    if (!cards?.length) {
      return;
    }

    cards.forEach((card) => {
      const dataElement =
        card.querySelector(
          'script[data-variants]'
        );

      const variantInput =
        card.querySelector(
          'input[ref="variantId"]'
        );

      const selects = [
        ...card.querySelectorAll(
          '.mydex-cart-recommendation__select'
        ),
      ];

      if (
        !dataElement ||
        !variantInput ||
        !selects.length
      ) {
        return;
      }

      let variants;

      try {
        variants = JSON.parse(
          dataElement.textContent || '[]'
        );
      } catch (error) {
        console.warn(
          '[cart-drawer] Could not read recommendation variants:',
          error
        );
        return;
      }

      if (
        !Array.isArray(variants) ||
        !variants.length
      ) {
        return;
      }

      const availableVariants =
        variants.filter(
          (variant) => variant?.available
        );

      if (!availableVariants.length) {
        selects.forEach(
          (select) => {
            select.disabled = true;
          }
        );

        return;
      }

      const getOptions = (variant) => {
        if (
          Array.isArray(
            variant?.options
          )
        ) {
          return variant.options;
        }

        return [
          variant?.option1,
          variant?.option2,
          variant?.option3,
        ].filter(
          (option) => option != null
        );
      };

      const setVariant = (variant) => {
        if (!variant) {
          return;
        }

        const options =
          getOptions(variant);

        selects.forEach(
          (select, index) => {
            if (
              options[index] != null
            ) {
              select.value =
                String(
                  options[index]
                );
            }
          }
        );

        variantInput.value =
          String(variant.id);
      };

      const findSelectedVariant = () => {
        const selected =
          selects.map(
            (select) => select.value
          );

        return availableVariants.find(
          (variant) => {
            const options =
              getOptions(variant);

            return selects.every(
              (select, index) =>
                String(
                  options[index] ?? ''
                ) ===
                String(
                  selected[index] ?? ''
                )
            );
          }
        );
      };

      const updateAvailableOptions = () => {
        const selected =
          selects.map(
            (select) => select.value
          );

        selects.forEach(
          (select, optionIndex) => {
            [...select.options].forEach(
              (option) => {
                const possible =
                  availableVariants.some(
                    (variant) => {
                      const options =
                        getOptions(
                          variant
                        );

                      return options.every(
                        (
                          variantOption,
                          index
                        ) => {
                          if (
                            index ===
                            optionIndex
                          ) {
                            return (
                              String(
                                variantOption ??
                                  ''
                              ) ===
                              String(
                                option.value
                              )
                            );
                          }

                          return (
                            String(
                              variantOption ??
                                ''
                            ) ===
                            String(
                              selected[index] ??
                                ''
                            )
                          );
                        }
                      );
                    }
                  );

                option.disabled =
                  !possible;
              }
            );
          }
        );
      };

      const updateVariant = () => {
        let variant =
          findSelectedVariant();

        if (!variant) {
          const firstCompatible =
            availableVariants.find(
              (candidate) => {
                const options =
                  getOptions(
                    candidate
                  );

                return selects.every(
                  (select, index) => {
                    const current =
                      select.value;

                    return (
                      !current ||
                      String(
                        options[index] ??
                          ''
                      ) ===
                      String(current)
                    );
                  }
                );
              }
            );

          variant =
            firstCompatible ||
            availableVariants[0];

          setVariant(variant);
        } else {
          variantInput.value =
            String(variant.id);
        }

        updateAvailableOptions();
      };

      const initialVariant =
        availableVariants.find(
          (variant) =>
            String(variant.id) ===
            String(variantInput.value)
        ) ||
        availableVariants[0];

      setVariant(initialVariant);
      updateVariant();

      selects.forEach(
        (select) => {
          select.addEventListener(
            'change',
            updateVariant
          );
        }
      );
    });
  };


  #getRecommendationHost() {
    const content =
      this.querySelector(
        '.cart-drawer__content'
      );

    const shippingBar =
      content?.querySelector(
        '.mydex-shipping-bar'
      );

    if (
      !content ||
      !shippingBar
    ) {
      return null;
    }

    let host =
      content.querySelector(
        '.mydex-cart-recommendations-host'
      );

    if (!host) {
      host =
        document.createElement(
          'div'
        );

      host.className =
        'mydex-cart-recommendations-host';

      shippingBar.insertAdjacentElement(
        'afterend',
        host
      );
    }

    return host;
  }


  #waitForCartSectionUpdate = (detail) => {
    const expectedItems = Array.isArray(detail?.items) ? detail.items : null;

    if (!expectedItems) {
      return new Promise((resolve) => {
        requestAnimationFrame(() => requestAnimationFrame(resolve));
      });
    }

    const expectedKeys = new Set(
      expectedItems
        .map((item) => String(item?.key || ''))
        .filter(Boolean)
    );

    return new Promise((resolve) => {
      const startedAt = performance.now();
      const timeout = 2000;

      const check = () => {
        const rows = [
          ...this.querySelectorAll('.cart-items__table-row[data-key]'),
        ];

        const currentKeys = new Set(
          rows
            .map((row) => String(row.dataset.key || ''))
            .filter(Boolean)
        );

        const isSynced =
          currentKeys.size === expectedKeys.size &&
          [...expectedKeys].every((key) => currentKeys.has(key));

        if (isSynced || performance.now() - startedAt >= timeout) {
          requestAnimationFrame(() => resolve());
          return;
        }

        requestAnimationFrame(check);
      };

      check();
    });
  };

  #handleCartLinesUpdate = (event) => {
    const shouldAutoOpen = this.hasAttribute('auto-open') && event.action === 'add' && !this.#themeDrawer?.isOpen;

    // When the event originates inside an open MODAL <dialog> (e.g. quick-add),
    // defer the auto-open until that dialog's native `close` fires so its focus
    // restoration runs first — otherwise we'd capture the wrong
    // `#previouslyFocused`. Non-modal dialogs (e.g. the hotspot preview) don't
    // close on add and don't move focus, so `:modal` excludes them.
    const sourceModal = /** @type {HTMLDialogElement | null} */ (
      event.target instanceof Element ? event.target.closest('dialog:modal') : null
    );

    if (shouldAutoOpen && !sourceModal && !this.#isCartEmpty()) {
      this.#themeDrawer?.open();
    }

    event.promise
      ?.then(async ({ detail }) => {
        if (!detail?.didError) {
          await this.#waitForCartSectionUpdate(detail);
        }

        const settle = () => requestAnimationFrame(() => {
          this.#updateStickyState();
          this.#loadRecommendations();
        });

        if (!shouldAutoOpen || detail?.didError) {
          settle();
          return;
        }

        const openAndSettle = () => {
          if (!this.#themeDrawer?.isOpen) this.#themeDrawer?.open();
          settle();
        };

        if (sourceModal?.open) {
          sourceModal.addEventListener('close', openAndSettle, { once: true });
        } else {
          openAndSettle();
        }
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[cart-drawer] Event promise rejected:', error);
      });
  };

  #isCartEmpty() {
    return Boolean(this.querySelector('.cart-drawer--empty'));
  }

  #updateStickyState() {
    const dialog = this.#dialog;
    if (!dialog) return;

    // Refs do not cross nested `*-component` boundaries (e.g., `cart-items-component`), so we query within the dialog.
    const content = dialog.querySelector('.cart-drawer__content');
    const summary = dialog.querySelector('.cart-drawer__summary');

    if (!content || !summary) {
      // Ensure the dialog doesn't get stuck in "unsticky" mode when summary disappears (e.g., empty cart).
      dialog.setAttribute('cart-summary-sticky', 'false');
      return;
    }

    const drawerHeight = dialog.getBoundingClientRect().height;
    const summaryHeight = summary.getBoundingClientRect().height;
    const ratio = summaryHeight / drawerHeight;
    dialog.setAttribute('cart-summary-sticky', ratio > this.#summaryThreshold ? 'false' : 'true');
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}
